import { useSidebarState } from '@src/hooks/useSidebarState';
import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

/**
 * PLAT-11037 — the deep-link restore veil for the Translation Review workspace.
 *
 * A shared review link (`?translationReview` / `?clipTranslationReview`,
 * PLAT-10948 — presence opens, the value is the granularity)
 * cannot open the workspace on the first paint: the review needs data the
 * surface is still fetching — the clip entry waits for `useClipPreviewRenderer`
 * to boot the clip-scoped editor + adapter, and the Content Studio entry waits
 * for the org's supported languages and LaunchDarkly. So the surface underneath
 * (the clip preview, or the Transcript & Speakers page) paints first and the
 * reviewer watches it for a beat before the review drops over it.
 *
 * This paints the workspace's OWN backdrop over that window, at the workspace's
 * own z-index, so the recipient of a link never sees the surface behind it — the
 * veil simply gives way to the real workspace, which mounts with the same
 * backdrop already in place.
 *
 * The caller owns the lifetime: render it only while a review restore is known
 * to be pending, and stop when the workspace can mount. It deliberately has no
 * internal timeout — an unbounded veil would be a worse failure than the flash
 * it replaces, so the gating condition must be one that always resolves.
 */
export const TranslationReviewRestoreVeil: React.FC<{
  /** Match the workspace's z-index on this surface so the swap is invisible. */
  zIndex: number;
  /**
   * Match the workspace's layout too (PLAT-11649): a `page` review is a panel
   * beside the app sidebar on the app background, so its veil is the same
   * panel — the sidebar stays visible through the wait.
   */
  layout?: 'modal' | 'page';
}> = ({ zIndex, layout = 'modal' }) => {
  const { sidebarWidth } = useSidebarState();
  const isPage = layout === 'page';
  return createPortal(
    <Veil
      role="status"
      aria-live="polite"
      $page={isPage}
      style={{ zIndex, left: isPage ? sidebarWidth : 0 }}
    >
      <Label>Opening Translation Review…</Label>
    </Veil>,
    document.body,
  );
};

// Deliberately the same backdrop as the workspace's `Backdrop` (modal) or its
// `Surface` (page) — the veil and the workspace that replaces it must be
// indistinguishable, so these values have to track those.
const Veil = styled.div<{ $page: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props =>
    props.$page ? 'var(--background)' : 'rgba(0, 0, 0, 0.5)'};
  backdrop-filter: ${props => (props.$page ? 'none' : 'var(--modal-blur)')};
  border-left: ${props => (props.$page ? '1px solid var(--border)' : 'none')};
`;

const Label = styled.span`
  font-size: var(--text-sm);
  color: var(--text-muted);
`;
